import { ChangeDetectionStrategy, Component, ElementRef, Input, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Router } from '@angular/router';
import { CoursesService, PathsService, RestApiService, UrlService } from '@draeger-academy/common';
import { AuthenticationService } from '@draeger-academy/common/lib/services/authentication/authentication.service';
import { CartService } from '@draeger-academy/common/lib/services/cart/cart.service';
import { ConfigurationService } from '@draeger-academy/common/lib/services/configuration/configuration.service';
import { GenericModalService } from '@draeger-academy/presentation/lib/modules/generic-modal';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { TranslateService } from '@ngx-translate/core';
import { CartItem } from 'models/cartItem';
import { Course } from 'models/course';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'da-course-card',
    templateUrl: './course-card.component.html',
    styleUrls: ['./course-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CourseCardComponent implements OnInit {
    @Input() course: Course;
    @Input() allCategories: any;
    public dynamicParameter: string;
    public language: string;
    public courseDetail: string;
    public booking: string;
    public courseImage: string;
    public alt = '';
    public currency = '';
    public show = false;
    public format = '';
    public languageCode = '';
    public showWarning = false;
    public isNew = false;
    public isBestSeller = false;
    public isWebinar = false;
    public courseTitle = '';
    public lastWord = '';
    public clickAddToCart = false;

    @ViewChildren('event') event: QueryList<ElementRef>;
    @ViewChild('warningPrebooking', { static: true }) private _warnPrebooking;

    private _modalId: string;
    @ViewChild('overlay', { static: true }) private _overlay;
    @ViewChild('warn', { static: true }) private _warn;
    @ViewChild('requirementsModal', { static: true }) private _requirementsModal;

    private _$courseImage: BehaviorSubject<{}> = new BehaviorSubject<{}>(null);
    private _$course: BehaviorSubject<Course> = new BehaviorSubject<Course>(null);
    private _$events: BehaviorSubject<CartItem[]> = new BehaviorSubject<CartItem[]>([]);
    private _$hasEventsInitially: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private _$translations: BehaviorSubject<any> = new BehaviorSubject({});

    constructor(
        private _translateService: TranslateService,
        public _configurationService: ConfigurationService,
        private _cartService: CartService,
        private _coursesService: CoursesService,
        private _localizeRouterService: LocalizeRouterService,
        private _router: Router,
        private _urlService: UrlService,
        private _restApiService: RestApiService,
        private authenticationService: AuthenticationService,
        private cartService: CartService,
        private _pathsService: PathsService,
        private _genericModalService: GenericModalService
    ) {
        _translateService.get('language').subscribe((res: string) => {
            this.language = res;
        });
    }

    public get courseImage$(): Observable<{}> {
        return this._$courseImage.asObservable();
    }

    public get course$(): Observable<Course> {
        return this._$course.asObservable();
    }

    public get events$(): Observable<CartItem[]> {
        return this._$events.asObservable();
    }

    public get hasEventsInitially$(): Observable<boolean> {
        return this._$hasEventsInitially.asObservable();
    }

    private _$showCartUpdate: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public get showCartUpdate$(): Observable<boolean> {
        return this._$showCartUpdate.asObservable();
    }

    private _$courseCategory: BehaviorSubject<string> = new BehaviorSubject('');
    public get courseCategory$(): Observable<string> {
        return this._$courseCategory.asObservable();
    }

    private _$currencyPositionBefore: BehaviorSubject<string> = new BehaviorSubject('');
    public get currencyPositionBefore$(): Observable<string> {
        return this._$currencyPositionBefore.asObservable();
    }

    private _$showCartUpdatePrebooking: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public get showCartUpdatePrebooking$(): Observable<boolean> {
        return this._$showCartUpdatePrebooking.asObservable();
    }

    private _$showVat: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public get showVat$(): Observable<boolean> {
        return this._$showVat.asObservable();
    }

    private _$showLabels: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public get showLabels$(): Observable<boolean> {
        return this._$showLabels.asObservable();
    }

    private _$showShortDesc: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public get showShortDesc$(): Observable<boolean> {
        return this._$showShortDesc.asObservable();
    }

    ngOnInit() {
        this._translateService.get('keyWords'.toLowerCase()).subscribe((data) => {
            this._$translations.next(data);
        });

        if (this.course) {
            this.courseDetail = this._pathsService.getCourseDetailLink(this.course.id);
            this.booking = this._pathsService.getBookingLink(this.course.id);
            this._$course.next(this.course);
            if (this.course.image) {
                if (this.course.image.uri) {
                    this.courseImage = environment.APIEndpoint + this.course.image.uri;
                    this._$courseImage.next(this.courseImage);
                }
                if (this.course.image && this.course.image.fileName) {
                    this.alt = this.course.image.fileName;
                }
            }

            this._$hasEventsInitially.next(this.course && this.course.events && this.course.events.length > 0);

            if (this._configurationService.hasFeature('keyWords')) {
                this._$showLabels.next(true);
            }

            if (this.course.keyWords) {
                if (this.course.keyWords && this.course.keyWords.length > 0) {
                    this._translateService.get('keyWords').subscribe((keyWords) => {
                        const newLabel = keyWords['new'] ? keyWords['new'] : 'New';
                        if (this.course.keyWords[0].toUpperCase() === newLabel.toUpperCase()) {
                            this.isNew = true;
                        }

                        const bestseller = keyWords['bestseller'] ? keyWords['bestseller'] : 'Bestseller';
                        if (this.course.keyWords[0].toUpperCase() === bestseller.toUpperCase()) {
                            this.isBestSeller = true;
                        }

                        const webinar = keyWords['webinar'] ? keyWords['webinar'] : 'Webinar';
                        if (this.course.keyWords[0].toUpperCase() === webinar.toUpperCase()) {
                            this.isWebinar = true;
                        }
                    });
                }
            }
        }

        if (this._configurationService.isPartnerPage()) {
            this._translateService.stream('common').subscribe((translation) => {
                this.currency = translation.currency.value;
                this.format = translation.currency.format;
                this.languageCode = translation.currency.language;
                this._$currencyPositionBefore.next(translation.currencyBeforePrice);
            });
        } else {
            this._configurationService.$siteSettings.subscribe((data) => {
                if (data && data.length > 0) {
                    this.currency = data.filter((f) => f.key.toLowerCase() === 'currency')[0].value;
                    this._$currencyPositionBefore.next(data.filter((f) => f.key.toLowerCase() === 'currency')[0].currencyBeforePrice);
                    this.format = data.filter((f) => f.key.toLowerCase() === 'currency')[0].format
                        ? data.filter((f) => f.key.toLowerCase() === 'currency')[0].format
                        : '';
                    this.languageCode = data.filter((f) => f.key.toLowerCase() === 'currency')[0].language
                        ? data.filter((f) => f.key.toLowerCase() === 'currency')[0].language
                        : '';
                }
            });
        }

        if (this.allCategories && this.allCategories.length > 0) {
            const category = this.allCategories.filter((f) => {
                if (f.id) {
                    f.id.toString() === this.course.categoryId;
                }
            })[0];
            if (category && category.name) {
                this._$courseCategory.next(category.name);
            }
        }

        const _isPortal = this._configurationService.isPartnerPage();
        this._configurationService.portalVersion$.subscribe((portalVersion) => {
            this._checkIfWarningShouldBeShown(_isPortal, portalVersion);
        });

        if (this._configurationService.hasFeature('ShowVATPrice')) {
            this._$showVat.next(true);
        }
        if (this._configurationService.hasFeature('ShortDescription')) {
            this._$showShortDesc.next(true);
        }

        if (
            screen.width > 768 &&
            this._configurationService.hasFeature('keyWords') &&
            this.course.keyWords &&
            this.course.keyWords.length > 0
        ) {
            const words = this.course.name.split(' ');
            const lastStr = words[words.length - 1];
            const restOfWords = words.pop();
            this.lastWord = lastStr;
            words.forEach((element) => (this.courseTitle = this.courseTitle + ' ' + element));
        } else {
            if (this.course) {
                this.courseTitle = this.course.name;
            }
        }
    }

    public closeModal() {
        if (this._modalId) {
        }
    }

    public toggleEvents(element) {
        this.show = !this.show;
        const loader = element.querySelectorAll('.drg-loading-container')[0];

        if (this.show) {
            loader.classList.add('active');
            this._coursesService.getCourseById(this.course.id).then((course) => {
                if (course && course.events && course.events.length > 0) {
                    this._$events.next(course.events);
                } else {
                    this._$events.next([]);
                }
                setTimeout(() => {
                    if (element.nextSibling && element.nextSibling.style) {
                        element.nextSibling.style.maxHeight = element.nextSibling.scrollHeight + 3 + 'px';
                    }
                    element.classList.add('active');
                    loader.classList.remove('active');
                }, 50);
            });
        } else {
            if (element.nextSibling && element.nextSibling.style) {
                element.nextSibling.style.maxHeight = null;
            }
            element.classList.remove('active');
            loader.classList.remove('active');
        }
    }

    public addCourseToCart() {
        let _site = this._configurationService.getCurrentSite();
        if (this._configurationService.isPartnerPage()) {
            this._configurationService.currentPortal$.subscribe((portalId) => {
                if (portalId) {
                    _site = portalId;
                }
            });
        }
        this.clickAddToCart = true;
        this._cartService.ordersent = false;
        const showWarningTest = this.checkPrerequirements();
        if (
            this.authenticationService.isLoggedIn() &&
            showWarningTest &&
            this.showWarning &&
            this.checkPrerequirements() &&
            this.getBoolean(localStorage.getItem(`popup${this.course.id}`))
        ) {
            localStorage.setItem(`popup${this.course.id}`, 'true');
        } else {
            if (localStorage.getItem(`cartItems${_site}`)) {
                const cartItems = JSON.parse(this._urlService.decode(localStorage.getItem(`cartItems${_site}`)));

                if (
                    (cartItems &&
                        cartItems.courses &&
                        cartItems.courses.filter((courseItem) => this.course.id === courseItem) &&
                        cartItems.courses.filter((courseItem) => this.course.id === courseItem).length) ||
                    (cartItems &&
                        cartItems.wishlist &&
                        cartItems.wishlist.filter((courseItem) => this.course.id === courseItem) &&
                        cartItems.wishlist.filter((courseItem) => this.course.id === courseItem).length)
                ) {
                    if (this.clickAddToCart) {
                        this._overlayWarnHandler();
                    }
                } else {
                    this._cartService.addCourseToCart(this.course.id);
                    this._overlayNotification();
                }
            } else {
                this._cartService.addCourseToCart(this.course.id);
                this._overlayNotification();
            }
        }
        this.clickAddToCart = false;
    }
    private _overlayWarnHandler(): void {
        this._modalId = this._genericModalService.create(this._warn);
        this._genericModalService.open(this._modalId, this._warn, true);
    }

    private async _overlayNotificationPrebooking() {
        this._$showCartUpdatePrebooking.next(true);
        setTimeout(() => this._$showCartUpdatePrebooking.next(false), 4000);
    }

    public addToWishlist() {
        let _site = this._configurationService.getCurrentSite();
        if (this._configurationService.isPartnerPage()) {
            this._configurationService.currentPortal$.subscribe((portalId) => {
                if (portalId) {
                    _site = portalId;
                }
            });
        }
        this.clickAddToCart = true;
        this._cartService.ordersent = false;
        if (localStorage.getItem(`cartItems${_site}`)) {
            const cartItems = JSON.parse(this._urlService.decode(localStorage.getItem(`cartItems${_site}`)));

            const eventInCart = [];
            this.course.events.forEach((el) => {
                eventInCart.push(cartItems.events.filter((item) => el.externalId === item));
            });

            if (
                cartItems &&
                cartItems.wishlist &&
                cartItems.wishlist.filter((courseItem) => this.course.id === courseItem) &&
                cartItems.wishlist.filter((courseItem) => this.course.id === courseItem).length
            ) {
                if (this.clickAddToCart) {
                    this._overlayWarnWishlistHandler();
                }
            } else {
                this._cartService.addCourseToWishlist(this.course.id);
                this._overlayNotificationPrebooking();
            }
        } else {
            this._cartService.addCourseToWishlist(this.course.id);
            this._overlayNotificationPrebooking();
        }
        this.clickAddToCart = false;
    }

    private _overlayWarnWishlistHandler(): void {
        this._modalId = this._genericModalService.create(this._warnPrebooking);
        this._genericModalService.open(this._modalId, this._warnPrebooking, true);
    }

    public goToCart() {
        this._$showCartUpdate.next(false);
        this.closeModal();
        this._cartService.goToCart();
    }
    public checkPrerequirements() {
        if (!localStorage.getItem(`popup${this.course.id}`) || this.getBoolean(localStorage.getItem(`popup${this.course.id}`))) {
            localStorage.setItem(`popup${this.course.id}`, 'true');
            return false;
        } else {
            if (this.course.id && this.showWarning) {
                const result = this.cartService.checkPrerequirements(this.course.id);
                return result;
                // this.cartService.checkPrerequirements(this.course.id).then(
                //     (data) => {
                //         return data;
                //     },
                //     (error) => {
                //         return true;
                //     }
                // );
            } else {
                return true;
            }
        }
    }

    private _checkIfWarningShouldBeShown(isPortal = false, portalVersion: string): void {
        const _shouldHaveCheckPrerequest =
            (!isPortal && this._configurationService.hasFeature('CheckPrerequest')) || (portalVersion === 'v2' && isPortal);
        this.showWarning = _shouldHaveCheckPrerequest;
    }

    public goToCourseDetails() {
        const _detailPath = this._pathsService.getCourseDetailLink(this.course.id);
        this._router.navigate([_detailPath]);
    }

    private async _overlayNotification() {
        this._$showCartUpdate.next(true);
        setTimeout(() => this._$showCartUpdate.next(false), 4000);
    }

    private getBoolean(value) {
        switch (value) {
            case true:
            case 'true':
            case 1:
            case '1':
            case 'on':
            case 'yes':
                return true;
            default:
                return false;
        }
    }

    public goToSimilarCourse() {
        window.location.href = this.courseDetail;
    }
}
